import React from 'react';
import { ENV_COLORS, ENV_SHAPES, ENV_DEFS } from './EnvironmentParts';
import type { EnvironmentAnimationState } from '../../types';

interface EnvironmentProps {
  animationState: EnvironmentAnimationState;
  quality: 'low' | 'balanced' | 'high';
  reducedMotion: boolean;
  width: number;
  height: number;
}

export function Environment({ animationState, quality, reducedMotion, width, height }: EnvironmentProps) {
  const { causticPhase, bubblePhases, rayPhase } = animationState;

  const bubbleCount = quality === 'high' ? 14 : quality === 'balanced' ? 9 : 5;
  const activeBubblePhases = bubblePhases.slice(0, bubbleCount);

  const bubblePaths = [
    ENV_SHAPES.bubblePath1,
    ENV_SHAPES.bubblePath2,
    ENV_SHAPES.bubblePath3,
    ENV_SHAPES.bubblePath4,
    ENV_SHAPES.bubblePath5,
    ENV_SHAPES.bubblePath6,
  ];

  const bubbles = activeBubblePhases.map((phase, i) => {
    const path = bubblePaths[i % bubblePaths.length];
    const progress = (phase % (Math.PI * 2)) / (Math.PI * 2);
    const y = 400 - progress * 420;
    const baseX = 60 + (i * 95) % 480;
    const x = baseX + Math.sin(phase * 1.5) * 15;
    const size = 2.5 + (i % 5) * 1.8;
    const opacity = 0.35 * (1 - progress * 0.4);

    return (
      <g key={i} transform={`translate(${x - 100}, ${y - 400})`}>
        <path
          d={path}
          fill="url(#bubble-gradient)"
          opacity={opacity}
          className="env-bubble"
        />
      </g>
    );
  });

  const causticTransform = reducedMotion
    ? ''
    : `translate(${Math.sin(causticPhase) * 35}px, ${Math.cos(causticPhase * 0.7) * 25}px)`;

  const rayTransform = reducedMotion
    ? ''
    : `translateX(${Math.sin(rayPhase) * 50}px) skewX(-8deg)`;

  return (
    <svg
      viewBox="0 0 600 400"
      className="lab-environment"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'visible',
      }}
      role="img"
      aria-label="ThriveCove premium aquarium environment"
      preserveAspectRatio="xMidYMid slice"
    >
      {ENV_DEFS}

      <rect
        width="600"
        height="400"
        fill="url(#water-gradient)"
        className="env-water"
      />

      <g className="env-depth-haze">
        <ellipse
          cx="300"
          cy="320"
          rx="400"
          ry="100"
          fill="url(#depth-haze-1)"
        />
        <ellipse
          cx="300"
          cy="400"
          rx="500"
          ry="150"
          fill="url(#depth-haze-2)"
        />
      </g>

      <g className="env-water-rays" transform={rayTransform} style={{ opacity: 0.06 }}>
        <path
          d={ENV_SHAPES.waterRays}
          fill={ENV_COLORS.rayColor}
          filter="url(#water-blur)"
        />
      </g>

      <g className="env-caustics" transform={causticTransform} style={{ opacity: 0.05 }}>
        <path
          d={ENV_SHAPES.causticPattern}
          fill="none"
          stroke="url(#caustic-glow)"
          strokeWidth="40"
          strokeLinecap="round"
          filter="url(#water-blur)"
        />
      </g>

      <g className="env-distant" style={{ opacity: 0.35, filter: 'url(#distant-blur)' }}>
        <path d={ENV_SHAPES.distantRock1} fill={ENV_COLORS.distantRock} />
        <path d={ENV_SHAPES.distantRock2} fill={ENV_COLORS.distantRock} />
        <path d={ENV_SHAPES.distantCoral1} fill={ENV_COLORS.distantCoral1} />
        <path d={ENV_SHAPES.distantCoral2} fill={ENV_COLORS.distantCoral2} />
        <path d={ENV_SHAPES.distantCoral3} fill={ENV_COLORS.distantCoral3} />
        <path d={ENV_SHAPES.distantCoral4} fill={ENV_COLORS.distantCoral1} opacity="0.6" />
      </g>

      <g className="env-midground" style={{ filter: 'url(#water-blur)' }}>
        <path d={ENV_SHAPES.midgroundArch} fill={ENV_COLORS.midgroundArch} opacity="0.7" />
        <path d={ENV_SHAPES.midgroundRock1} fill={ENV_COLORS.midgroundRock1} />
        <path d={ENV_SHAPES.midgroundRock2} fill={ENV_COLORS.midgroundRock2} />
        <path d={ENV_SHAPES.midgroundCoral1} fill={ENV_COLORS.midgroundCoral1} opacity="0.8" />
        <path d={ENV_SHAPES.midgroundCoral2} fill={ENV_COLORS.midgroundCoral2} opacity="0.8" />
        <path d={ENV_SHAPES.midgroundCoral3} fill={ENV_COLORS.midgroundCoral3} opacity="0.75" />
      </g>

      <path
        d={ENV_SHAPES.substrate}
        fill="url(#substrate-gradient)"
        className="env-substrate"
      />
      <ellipse
        cx="300"
        cy="335"
        rx="300"
        ry="40"
        fill="#0d3d3d"
        opacity="0.12"
      />

      <g className="env-foreground" style={{ filter: 'url(#water-blur)' }}>
        <path d={ENV_SHAPES.foregroundRock1} fill={ENV_COLORS.foregroundRock1} />
        <path d={ENV_SHAPES.foregroundRock2} fill={ENV_COLORS.foregroundRock2} />
        <path d={ENV_SHAPES.foregroundRock3} fill={ENV_COLORS.foregroundRock3} />
        <path d={ENV_SHAPES.foregroundCoral1} fill={ENV_COLORS.foregroundCoral1} opacity="0.85" />
        <path d={ENV_SHAPES.foregroundCoral2} fill={ENV_COLORS.foregroundCoral2} opacity="0.85" />
        <path d={ENV_SHAPES.foregroundCoral3} fill={ENV_COLORS.foregroundCoral3} opacity="0.8" />
        <path d={ENV_SHAPES.foregroundPlant1} fill={ENV_COLORS.foregroundPlant1} opacity="0.7" />
        <path d={ENV_SHAPES.foregroundPlant2} fill={ENV_COLORS.foregroundPlant2} opacity="0.7" />
        <path d={ENV_SHAPES.foregroundPlant3} fill={ENV_COLORS.foregroundPlant3} opacity="0.65" />
      </g>

      <g className="env-bubbles" style={{ willChange: reducedMotion ? 'auto' : 'transform' }}>
        {bubbles}
      </g>
    </svg>
  );
}